import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Plus, Upload, Copy } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const REJECT_REASONS = ["spam_or_advertising", "profanity_hate_harassment", "personal_or_private_info",
  "unrelated_to_hola_movers", "not_a_real_experience", "written_by_staff_or_family", "submitted_without_consent"];

function MemberEditor({ m, onSave, onDelete }) {
  const [d, setD] = useState(m);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setQ = (i, k, v) => setD((x) => ({ ...x, questions: x.questions.map((q, j) => j === i ? { ...q, [k]: v } : q) }));
  const addQ = () => setD((x) => ({ ...x, questions: [...(x.questions || []), { id: `q${Date.now()}`, text_en: "", text_es: "" }] }));
  const delQ = (i) => setD((x) => ({ ...x, questions: x.questions.filter((_, j) => j !== i) }));
  const cls = "w-full border border-sand-200 rounded-lg px-3 py-2 text-sm";
  const isSaved = d.id && d.id !== "__new__";
  const vouchUrl = isSaved ? `${window.location.origin}/vouch/${d.id}` : "";
  const invite = isSaved
    ? `Hi! I help with Hola Movers (pet-friendly moves to Mexico). Would you share a short reference about ${d.name}? It takes 2 minutes: ${vouchUrl}`
    : "";
  const inviteEs = isSaved
    ? `¡Hola! Colaboro con Hola Movers (mudanzas a México que cuidan a tus mascotas). ¿Podrías dejar una referencia breve sobre ${d.name}? Toma 2 minutos: ${vouchUrl}`
    : "";

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !isSaved) { if (!isSaved) toast.error("Save the member first, then upload a photo."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post(`/admin/team-members/${d.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", r.data.url);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  return (
    <Card className="p-4 space-y-3" data-testid={`member-edit-${d.id}`}>
      <div className="flex items-start gap-4">
        {d.photo ? <img src={d.photo} alt={d.name} className="h-16 w-16 rounded-full object-cover shrink-0" /> : <div className="h-16 w-16 rounded-full bg-sand-100 shrink-0" />}
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <input data-testid={`member-name-${d.id}`} className={cls} placeholder="Name" value={d.name || ""} onChange={(e) => set("name", e.target.value)} />
            <input className={cls} placeholder="Role (EN)" value={d.role_en || ""} onChange={(e) => set("role_en", e.target.value)} />
            <input className={cls} placeholder="Role (ES)" value={d.role_es || ""} onChange={(e) => set("role_es", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <label data-testid={`member-photo-upload-${d.id}`} className="text-xs border rounded-full px-3 py-1.5 cursor-pointer flex items-center gap-1 hover:bg-sand-50">
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} disabled={uploading} />
            </label>
            <input className={cls + " flex-1"} placeholder="…or paste a photo URL" value={d.photo || ""} onChange={(e) => set("photo", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="flex gap-4 text-sm">
        <label data-testid={`member-accepting-${d.id}`} className="flex items-center gap-2"><input type="checkbox" checked={!!d.accepting_vouches} onChange={(e) => set("accepting_vouches", e.target.checked)} /> Accepting references</label>
        <label data-testid={`member-active-${d.id}`} className="flex items-center gap-2"><input type="checkbox" checked={d.active !== false} onChange={(e) => set("active", e.target.checked)} /> Active</label>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-medium text-charcoal-500">Questions</p>
        {(d.questions || []).map((q, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input className={cls} placeholder="EN" value={q.text_en} onChange={(e) => setQ(i, "text_en", e.target.value)} />
            <input className={cls} placeholder="ES" value={q.text_es} onChange={(e) => setQ(i, "text_es", e.target.value)} />
            <button data-testid={`member-delq-${d.id}-${i}`} onClick={() => delQ(i)} className="text-red-500 mt-2"><Trash2 size={14} /></button>
          </div>
        ))}
        <button data-testid={`member-addq-${d.id}`} onClick={addQ} className="text-xs border rounded-full px-3 py-1 flex items-center gap-1"><Plus size={12} /> Add question</button>
      </div>

      {isSaved && (
        <div className="rounded-xl bg-sand-50 border border-sand-200 p-3 space-y-2" data-testid={`member-invite-${d.id}`}>
          <p className="text-xs font-medium text-charcoal-600">Invite friends to leave a reference for {d.name}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-sand-200 rounded px-2 py-1.5 break-all">{vouchUrl}</code>
            <button data-testid={`member-copy-link-${d.id}`} onClick={() => copy(vouchUrl)} className="text-xs border rounded-full px-3 py-1.5 flex items-center gap-1"><Copy size={12} /> Link</button>
          </div>
          <div className="flex items-start gap-2">
            <p className="flex-1 text-xs text-charcoal-500 italic">{invite}</p>
            <button data-testid={`member-copy-msg-${d.id}`} onClick={() => copy(invite)} className="text-xs border rounded-full px-3 py-1.5 flex items-center gap-1 shrink-0"><Copy size={12} /> EN</button>
          </div>
          <div className="flex items-start gap-2">
            <p className="flex-1 text-xs text-charcoal-500 italic">{inviteEs}</p>
            <button data-testid={`member-copy-msg-es-${d.id}`} onClick={() => copy(inviteEs)} className="text-xs border rounded-full px-3 py-1.5 flex items-center gap-1 shrink-0"><Copy size={12} /> ES</button>
          </div>
          <p className="text-[11px] text-charcoal-400">Share by WhatsApp, text or email. Each reference is reviewed before it appears publicly.</p>
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid={`member-save-${d.id}`} onClick={() => onSave(d)} className="bg-terracotta text-white rounded-full px-4 py-1.5 text-sm">Save</button>
        {onDelete && isSaved && <button onClick={() => onDelete(d.id)} className="border border-red-300 text-red-600 rounded-full px-4 py-1.5 text-sm">Deactivate</button>}
      </div>
    </Card>
  );
}

export default function ReferencesManager() {
  const [tab, setTab] = useState("references");
  const [status, setStatus] = useState("pending");
  const [vouches, setVouches] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState(null);

  const loadVouches = () => api.get(`/admin/vouches?status=${status}`).then((r) => setVouches(r.data)).catch(() => {});
  const loadMembers = () => api.get(`/admin/team-members`).then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { loadVouches(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { loadMembers(); }, []);

  const approve = async (id) => { await api.post(`/admin/vouches/${id}/approve`); toast.success("Approved"); loadVouches(); };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (never for being critical):\n" + REJECT_REASONS.join(", "));
    if (!reason) return;
    try { await api.post(`/admin/vouches/${id}/reject`, { reason }); toast.success("Rejected"); loadVouches(); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid reason"); }
  };
  const saveMember = async (d) => {
    try {
      if (members.find((x) => x.id === d.id)) await api.put(`/admin/team-members/${d.id}`, d);
      else { await api.post(`/admin/team-members`, d); setNewMember(null); }
      toast.success("Saved"); loadMembers();
    } catch { toast.error("Could not save"); }
  };
  const delMember = async (id) => { await api.delete(`/admin/team-members/${id}`); toast.success("Deactivated"); loadMembers(); };

  return (
    <div data-testid="admin-references">
      <SectionTitle title="References" subtitle="Personal references (vouches) for team members. Not client reviews — never counted in stars." />

      <div className="flex gap-2 mb-5 text-sm">
        {[["references", "References"], ["members", "Team members"]].map(([k, l]) => (
          <button key={k} data-testid={`ref-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 ${tab === k ? "bg-terracotta text-white" : "bg-sand-100 text-charcoal-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "references" && (
        <>
          <div className="flex gap-2 mb-4 text-sm">
            {["pending", "approved", "rejected", "removed", "all"].map((s) => (
              <button key={s} data-testid={`vouch-filter-${s}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 ${status === s ? "bg-terracotta text-white" : "bg-sand-100 text-charcoal-600"}`}>{s}</button>
            ))}
          </div>
          <div className="space-y-3">
            {vouches.map((v) => (
              <Card key={v.id} className="p-4" data-testid={`admin-vouch-${v.id}`}>
                <div className="flex items-center gap-2 mb-1 text-sm">
                  <span className="font-medium text-charcoal-800">{v.author_name}</span>
                  <span className="text-xs text-charcoal-400">for {v.member_name} · {v.relationship}{v.years_known ? ` · ${v.years_known}y` : ""} · {v.language}</span>
                  {v.author_email && <span className="text-xs text-charcoal-400">· {v.author_email}</span>}
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{v.status}{v.reject_reason ? ` · ${v.reject_reason}` : ""}</span>
                </div>
                <div className="text-sm text-charcoal-700 space-y-1 mt-2">
                  {(v.answers || []).map((a, i) => <p key={i}>{a.text}</p>)}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  {v.status !== "approved" && <button data-testid={`vouch-approve-${v.id}`} onClick={() => approve(v.id)} className="bg-green-600 text-white rounded-full px-3 py-1 flex items-center gap-1"><Check size={12} /> Approve</button>}
                  {v.status !== "rejected" && <button data-testid={`vouch-reject-${v.id}`} onClick={() => reject(v.id)} className="border border-red-300 text-red-600 rounded-full px-3 py-1 flex items-center gap-1"><X size={12} /> Reject</button>}
                </div>
              </Card>
            ))}
            {vouches.length === 0 && <p className="text-sm text-charcoal-500">No references in this view.</p>}
          </div>
        </>
      )}

      {tab === "members" && (
        <div className="space-y-4">
          {members.map((m) => <MemberEditor key={m.id} m={m} onSave={saveMember} onDelete={delMember} />)}
          {newMember ? (
            <MemberEditor m={newMember} onSave={saveMember} />
          ) : (
            <button data-testid="add-member" onClick={() => setNewMember({ id: "__new__", name: "", role_en: "", role_es: "", photo: "", accepting_vouches: true, active: true, questions: [] })} className="border rounded-full px-4 py-2 text-sm flex items-center gap-1"><Plus size={14} /> Add team member</button>
          )}
        </div>
      )}
    </div>
  );
}
